import {
  TEST_ECHO_IN_FLIGHT_STATUSES,
  type TestEchoLaunchStatusValue,
} from "../types";

/**
 * Turn a bot refusal into a sentence an operator can act on.
 *
 * The bot's contract (plan §3.5-B) is a status plus a machine reason:
 *   403 {"error":"forbidden","reason":"user_not_admin"}
 *   409 {"error":"busy","reason":"active_echo_exists|waiting_for_feedback|
 *                                 ml_processing|daily_cap_reached"}
 *   404 when the policy is off (or the CRM proxy allowlist rejected the path)
 *   401 bad key / no CRM session
 * Rendering "busy" or "forbidden" raw tells the operator nothing about what to
 * do next, so every known reason gets its own remedy. Kept pure and separate
 * from the card so it can be unit-tested without a browser.
 */
export function testEchoRefusalMessage(error: unknown): string {
  const status = numberField(error, "status");
  const payload = objectField(error, "payload");
  const code = stringField(error, "code") ?? stringOf(payload?.error);
  const reason = stringOf(payload?.reason);

  if (status === 401) {
    return "Your CRM session is not authorised for this. Sign in again as an administrator.";
  }
  if (status === 403 || code === "forbidden") {
    return reason && reason !== "user_not_admin"
      ? `The bot refused this launch (${reason}).`
      : "That WhatsApp number is not an admin on the bot, so it refused to launch an Echo for it.";
  }
  if (status === 404) {
    return "The test echo autoqueue is switched off in this environment (the bot returns 404 when the policy is off, and the CRM proxy only forwards /launches and /packs).";
  }
  if (reason === "route_ambiguous") {
    const keys = senderKeys(payload);
    return keys.length
      ? `That number has a conversation on more than one WhatsApp sender (${keys.join(", ")}). Pick the one to launch on.`
      : "That number has a conversation on more than one WhatsApp sender. Pick the one to launch on.";
  }
  if (status === 409 || code === "busy" || code === "route") {
    return BUSY_REASONS[reason ?? ""] ?? busyFallback(reason);
  }
  if (status === 500) {
    return "The CRM cannot reach the bot: TEST_ECHO_BOT_BASE_URL / INTERNAL_OPS_API_KEY are not set on this Vercel project.";
  }

  const message = stringField(error, "message");
  return message || "The launch failed. Check the bot logs for this operator.";
}

const BUSY_REASONS: Record<string, string> = {
  active_echo_exists:
    "That number already has an Echo in flight. Wait for it to finish (or cancel it from WhatsApp) before launching another.",
  waiting_for_feedback:
    "The bot is waiting for that number to rate its last Echo. Rate it on WhatsApp, or switch on “Clear the rating gate” and launch again.",
  ml_processing:
    "The bot is mid-turn for that number right now. Give it a moment and try again.",
  daily_cap_reached:
    "The daily test-echo cap for that number is used up. Raise it in the runtime policy, or wait for the counter to roll over.",
};

/**
 * The senders a `route_ambiguous` refusal offers to choose between.
 *
 * The bot cannot know which sender carries a given number's conversation, so
 * it refuses rather than guessing into the wrong Redis scope, and names the
 * candidates. Surfacing them turns a dead end into one click.
 */
export function testEchoSenderChoices(error: unknown): string[] {
  const payload = objectField(error, "payload");
  if (stringOf(payload?.reason) !== "route_ambiguous") return [];
  return senderKeys(payload);
}

function senderKeys(payload: Record<string, unknown> | null): string[] {
  const keys = payload?.sender_keys;
  if (!Array.isArray(keys)) return [];
  return keys
    .map((key) => stringOf(key))
    .filter((key): key is string => key !== null);
}

function busyFallback(reason: string | null) {
  return reason
    ? `The bot is busy with that number (${reason}).`
    : "The bot is busy with that number.";
}

const IN_FLIGHT = new Set<string>(TEST_ECHO_IN_FLIGHT_STATUSES);

/** True while a launch is still moving, so the card keeps polling it. */
export function isTestEchoInFlight(
  status?: TestEchoLaunchStatusValue | string | null,
): boolean {
  return IN_FLIGHT.has(String(status ?? ""));
}

export const TEST_ECHO_POLL_MS = 15_000;

/**
 * React Query `refetchInterval` for one launch: an interval ONLY while the
 * launch is in flight, `false` once it is terminal (same rule as the Remakes
 * card), so a finished launch stops hitting the bot through the proxy.
 */
export function testEchoRefetchInterval(
  status?: TestEchoLaunchStatusValue | string | null,
): number | false {
  return isTestEchoInFlight(status) ? TEST_ECHO_POLL_MS : false;
}

const STATUS_LABELS: Record<string, string> = {
  interviewing: "Interviewing",
  generating: "Generating",
  delivered: "Delivered",
  failed: "Failed",
  unknown: "Unknown",
};

export function testEchoStatusLabel(status?: string | null): string {
  const value = String(status ?? "unknown");
  return STATUS_LABELS[value] ?? value;
}

export function testEchoStatusVariant(
  status?: string | null,
): "default" | "secondary" | "destructive" | "outline" {
  if (status === "failed") return "destructive";
  if (status === "delivered") return "secondary";
  if (isTestEchoInFlight(status)) return "default";
  return "outline";
}

function stringOf(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function stringField(value: unknown, key: string): string | null {
  return stringOf((value as Record<string, unknown> | null)?.[key]);
}

function numberField(value: unknown, key: string): number | null {
  const field = (value as Record<string, unknown> | null)?.[key];
  return typeof field === "number" && Number.isFinite(field) ? field : null;
}

function objectField(
  value: unknown,
  key: string,
): Record<string, unknown> | null {
  const field = (value as Record<string, unknown> | null)?.[key];
  return typeof field === "object" && field !== null
    ? (field as Record<string, unknown>)
    : null;
}
